import React, { useState } from 'react';
import { Share2, Copy, Check, X, MessageCircle, Twitter, Facebook, Mail, Link2 } from 'lucide-react';

export const ShareRecipeButton = ({ recipe }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/recipe/${recipe.id}`;
  const shareTitle = `${recipe.title} - Recetik`;
  const shareText = `¡Mira esta receta increíble! ${recipe.title}: ${recipe.description}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        setIsOpen(false);
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    } else {
      setIsOpen(true);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-sky-500',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-stone-600',
      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
    }
  ];

  return (
    <div className="relative">
      <button
        data-testid="share-recipe-btn"
        onClick={handleNativeShare}
        className="flex items-center gap-2 px-6 py-4 rounded-2xl bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 font-black uppercase tracking-widest text-[10px] hover:bg-stone-200 dark:hover:bg-stone-700 transition-all"
      >
        <Share2 size={18} />
        Compartir
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="fixed inset-x-4 bottom-4 sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:mt-2 sm:w-80 bg-white dark:bg-stone-900 rounded-3xl shadow-2xl border border-stone-100 dark:border-stone-800 overflow-hidden z-50 animate-in slide-in-from-bottom-4 sm:slide-in-from-top-2 duration-300">
            <div className="p-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
              <p className="text-sm font-black text-stone-900 dark:text-white">
                Compartir Receta
              </p>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 text-stone-400 hover:text-stone-600 rounded-xl sm:hidden"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              {/* Social Share Buttons */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {shareOptions.map(option => (
                  <a
                    key={option.name}
                    href={option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors group"
                  >
                    <div className={`w-12 h-12 ${option.color} rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg`}>
                      <option.icon size={22} />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest text-stone-400">
                      {option.name}
                    </span>
                  </a>
                ))}
              </div>

              {/* Copy Link */}
              <div className="flex gap-2">
                <div className="flex-1 px-4 py-3 bg-stone-50 dark:bg-stone-800 rounded-2xl overflow-hidden">
                  <p className="text-xs text-stone-500 truncate font-mono">
                    {shareUrl}
                  </p>
                </div>
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center gap-2 ${
                    copied 
                      ? 'bg-green-500 text-white' 
                      : 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:bg-primary'
                  }`}
                >
                  {copied ? <Check size={16} /> : <Link2 size={16} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
